'use client';

import * as React from 'react';
import { normalizeMarkdownForRender } from '@mui/x-chat-headless/internals';
import { ChatCodeBlock } from "../ChatCodeBlock/index.mjs";

// ---------------------------------------------------------------------------
// Inline parser — bold, italic, inline-code, links
// ---------------------------------------------------------------------------
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const SAFE_URL_PROTOCOLS = ['http:', 'https:', 'mailto:'];
function sanitizeUrl(url) {
  try {
    const parsed = new URL(url);
    if (SAFE_URL_PROTOCOLS.includes(parsed.protocol)) {
      return url;
    }
  } catch {
    // Relative URLs (no protocol) are allowed through as-is
    if (!url.includes(':')) {
      return url;
    }
  }
  return '#';
}
const INLINE_PATTERNS = [{
  // Bold: **text** or __text__
  regex: /\*\*(.+?)\*\*|__(.+?)__/,
  render: (m, k) => /*#__PURE__*/_jsx("strong", {
    children: parseInline(m[1] ?? m[2])
  }, k)
}, {
  // Inline code: `text` — before italic so backtick content is not parsed further
  regex: /`([^`]+)`/,
  render: (m, k) => /*#__PURE__*/_jsx("code", {
    children: m[1]
  }, k)
}, {
  // Italic: *text* or _text_ (not preceded/followed by same char)
  regex: /(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)|(?<!_)_(?!_)(.+?)(?<!_)_(?!_)/,
  render: (m, k) => /*#__PURE__*/_jsx("em", {
    children: parseInline(m[1] ?? m[2])
  }, k)
}, {
  // Footnote citation: [^1] → superscript marker
  regex: /\[\^(\d+)\]/,
  render: (m, k) => /*#__PURE__*/_jsxs("sup", {
    children: ["[", m[1], "]"]
  }, k)
}, {
  // Link: [label](url)
  regex: /\[([^\]]+)\]\(([^)]+)\)/,
  render: (m, k) => /*#__PURE__*/_jsx("a", {
    href: sanitizeUrl(m[2]),
    target: "_blank",
    children: m[1]
  }, k)
}];
function parseInline(text) {
  if (!text) {
    return null;
  }
  const result = [];
  let remaining = text;
  let key = 0;
  while (remaining.length > 0) {
    let firstMatch = null;
    let firstPattern = null;
    for (const pattern of INLINE_PATTERNS) {
      const m = pattern.regex.exec(remaining);
      if (m && (firstMatch === null || m.index < firstMatch.index)) {
        firstMatch = m;
        firstPattern = pattern;
      }
    }
    if (!firstMatch || !firstPattern) {
      result.push(remaining);
      break;
    }
    if (firstMatch.index > 0) {
      result.push(remaining.slice(0, firstMatch.index));
    }
    result.push(firstPattern.render(firstMatch, key));
    key += 1;
    remaining = remaining.slice(firstMatch.index + firstMatch[0].length);
  }
  if (result.length === 0) {
    return null;
  }
  if (result.length === 1) {
    return result[0];
  }
  return /*#__PURE__*/_jsx(React.Fragment, {
    children: result
  });
}

// ---------------------------------------------------------------------------
// Block parser — code fences, headers, lists, paragraphs
// ---------------------------------------------------------------------------

function isStructuralLine(line) {
  return /^```/.test(line) || /^#{1,6}\s/.test(line) || /^[-*+]\s/.test(line) || /^\d+\.\s/.test(line);
}
function parseBlocks(text) {
  const lines = text.split('\n');
  const result = [];
  let key = 0;
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    const fenceMatch = /^```(\w*)/.exec(line);
    if (fenceMatch) {
      const lang = fenceMatch[1];
      const codeLines = [];
      i += 1;
      while (i < lines.length && !lines[i].startsWith('```')) {
        codeLines.push(lines[i]);
        i += 1;
      }
      i += 1;
      result.push(/*#__PURE__*/_jsx(ChatCodeBlock, {
        language: lang || undefined,
        children: codeLines.join('\n')
      }, key));
      key += 1;
      continue;
    }
    const headerMatch = /^(#{1,6})\s+(.+)/.exec(line);
    if (headerMatch) {
      const level = headerMatch[1].length;
      const Tag = `h${level}`;
      result.push(/*#__PURE__*/_jsx(Tag, {
        children: parseInline(headerMatch[2])
      }, key));
      key += 1;
      i += 1;
      continue;
    }
    if (/^[-*+]\s/.test(line)) {
      const items = [];
      while (i < lines.length && /^[-*+]\s/.test(lines[i])) {
        items.push(/*#__PURE__*/_jsx("li", {
          children: parseInline(lines[i].replace(/^[-*+]\s+/, ''))
        }, key));
        key += 1;
        i += 1;
      }
      result.push(/*#__PURE__*/_jsx("ul", {
        children: items
      }, key));
      key += 1;
      continue;
    }
    if (/^\d+\.\s/.test(line)) {
      const items = [];
      while (i < lines.length && /^\d+\.\s/.test(lines[i])) {
        items.push(/*#__PURE__*/_jsx("li", {
          children: parseInline(lines[i].replace(/^\d+\.\s+/, ''))
        }, key));
        key += 1;
        i += 1;
      }
      result.push(/*#__PURE__*/_jsx("ol", {
        children: items
      }, key));
      key += 1;
      continue;
    }
    if (line.trim() === '') {
      i += 1;
      continue;
    }
    const paraLines = [];
    while (i < lines.length && lines[i].trim() !== '' && !isStructuralLine(lines[i])) {
      paraLines.push(lines[i]);
      i += 1;
    }
    if (paraLines.length > 0) {
      result.push(/*#__PURE__*/_jsx("p", {
        children: parseInline(paraLines.join('\n'))
      }, key));
      key += 1;
    }
  }
  return result;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function renderMarkdown(text) {
  const normalized = normalizeMarkdownForRender(text);
  const blocks = parseBlocks(normalized);
  return blocks.length === 1 ? blocks[0] : /*#__PURE__*/_jsx(React.Fragment, {
    children: blocks
  });
}